/**
 * Hook pour la gestion des traductions des widgets
 *
 * Combine les traductions génériques (fichiers JSON) avec les traductions
 * personnalisables (API) pour fournir un système de traduction complet
 * avec fallback automatique.
 *
 * @module useWidgetTranslation
 */

import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

/**
 * Hook principal pour les traductions des widgets
 *
 * Fournit un système de traduction avec cascade de fallback :
 * 1. Traduction personnalisée dans la langue courante
 * 2. Traduction personnalisée dans la langue par défaut
 * 3. Traduction personnalisée en français
 * 4. Traduction générique (JSON via i18next)
 * 5. Chaîne vide
 *
 * Architecture simplifiée (v2.7.0+) :
 * Les traductions customisées sont maintenant embedded dans les configs widget
 * (RestaurantConfig.welcomeMessage/announcement/floatingButton pour reservation,
 * GiftVoucherSettings.floatingButton pour giftVoucher)
 *
 * @param {string} _apiKey - Clé API (legacy, non utilisé - pour compatibilité)
 * @param {string} _widgetType - Type de widget (legacy, non utilisé - pour compatibilité)
 * @param {Object} [companyDetails] - Détails de l'entreprise (pour widgets)
 * @param {Object} [widgetConfig] - Config widget contenant les traductions customisées
 * @return {Object} Utilitaires de traduction
 */
export const useWidgetTranslation = (_apiKey, _widgetType, companyDetails = null, widgetConfig = null) => {
  const {t, i18n} = useTranslation();

  // Si companyDetails fourni (widgets), utiliser directement la config
  // Sinon, fallback sur useMultiLanguage (dashboard)
  const multiLanguageConfig = useMemo(() => {
    if (companyDetails?.multiLanguage) {
      return {
        enabled: companyDetails.multiLanguage.enabled || false,
        defaultLanguage: companyDetails.multiLanguage.defaultLanguage || 'fr',
        enabledLanguages: companyDetails.multiLanguage.enabledLanguages || ['fr'],
      };
    }
    // Fallback pour le dashboard (pas utilisé dans les widgets)
    return {
      enabled: false,
      defaultLanguage: 'fr',
      enabledLanguages: ['fr'],
    };
  }, [companyDetails]);

  // Langue courante (de i18next ou défaut)
  const currentLanguage = i18n.language || multiLanguageConfig.defaultLanguage;

  // Extraction des traductions personnalisables depuis le config widget
  // Les widgets chargent maintenant leurs traductions depuis leur config
  // (ex: RestaurantConfig contient welcomeMessage, announcement.text, floatingButton)
  const customTranslations = useMemo(() => {
    if (!widgetConfig) return {};

    // Pour reservation widget: extraire welcomeMessage, announcement, floatingButton, messages personnalisés
    // Pour giftVoucher widget: extraire floatingButton
    return {
      welcomeMessage: widgetConfig.welcomeMessage || {},
      announcement: widgetConfig.announcement || {},
      floatingButton: widgetConfig.floatingButton || {},
      onlineBookingMessage: widgetConfig.reservationConstraints?.onlineBooking?.message || {},
      noticeClosedMessage: widgetConfig.bookingPolicy?.noticeClosedWarning?.message || {},
    };
  }, [widgetConfig]);

  /**
   * Récupère un texte personnalisable avec fallback automatique
   *
   * Gère les chemins imbriqués (ex: 'announcement.text') en naviguant récursivement
   * dans l'objet de traductions personnalisables.
   *
   * @param {string} fieldKey - Clé du champ personnalisable (ex: 'floatingButton', 'welcomeMessage', 'announcement.text')
   * @param {string} [genericKey] - Clé de la traduction générique dans les fichiers JSON
   * @returns {string} Texte traduit
   */
  const getCustomText = useCallback(
      (fieldKey, genericKey) => {
        // Gérer les chemins imbriqués (ex: 'announcement.text')
        const parts = fieldKey.split('.');
        let customField = customTranslations;

        // Naviguer dans l'objet imbriqué
        for (const part of parts) {
          if (!customField || typeof customField !== 'object') {
            customField = null;
            break;
          }
          customField = customField[part];
        }

        // Cascade de fallback pour les traductions personnalisables
        if (customField) {
          // 1. Langue courante
          if (customField[currentLanguage]?.trim()) {
            return customField[currentLanguage];
          }

          // 2. Langue par défaut (si différente de la langue courante)
          if (multiLanguageConfig.defaultLanguage &&
            multiLanguageConfig.defaultLanguage !== currentLanguage &&
            customField[multiLanguageConfig.defaultLanguage]?.trim()) {
            return customField[multiLanguageConfig.defaultLanguage];
          }

          // 3. Français en fallback
          if (customField.fr?.trim()) {
            return customField.fr;
          }
        }

        // 4. Traduction générique depuis les fichiers JSON (via i18next)
        if (genericKey) {
          const genericTranslation = t(genericKey);
          if (genericTranslation && genericTranslation !== genericKey) {
            return genericTranslation;
          }
        }

        // 5. Chaîne vide si aucune traduction trouvée
        return '';
      },
      [customTranslations, currentLanguage, multiLanguageConfig.defaultLanguage, t],
  );

  /**
   * Récupère une traduction générique depuis les fichiers JSON
   * Utile pour les éléments non personnalisables
   *
   * @param {string} key - Clé de traduction dans les fichiers JSON
   * @param {Object} [options] - Options de traduction (interpolation, etc.)
   * @returns {string} Texte traduit
   */
  const getGenericText = useCallback(
      (key, options = {}) => {
        return t(key, options);
      },
      [t],
  );

  /**
   * Récupère toutes les traductions personnalisables d'un widget
   *
   * @returns {Object} Objet contenant toutes les traductions personnalisables
   */
  const getAllCustomTexts = useCallback(() => {
    return customTranslations;
  }, [customTranslations]);

  /**
   * Change la langue courante
   *
   * @param {string} language - Code de la langue (fr, en, nl, de)
   */
  const changeLanguage = useCallback(
      (language) => {
        i18n.changeLanguage(language);
      },
      [i18n],
  );

  return {
    // Fonctions principales
    getCustomText,
    getGenericText,
    getAllCustomTexts,
    changeLanguage,

    // Alias pour compatibilité avec i18next
    t: getGenericText,

    // État
    currentLanguage,
    defaultLanguage: multiLanguageConfig.defaultLanguage,
    isMultiLanguageEnabled: multiLanguageConfig.enabled,
    enabledLanguages: multiLanguageConfig.enabledLanguages,
    isLoading: false, // Plus de chargement API, données depuis config
    isError: false,
    error: null,

    // Données brutes
    customTranslations,

    // Utilitaires (mutate non utilisé dans nouvelle architecture)
    mutate: () => {}, // No-op, pour compatibilité
  };
};

